import React, { useContext, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { IMaskInput } from 'react-imask';

import axios_api from '../Api';
import { API_URL, AUTH_TOKEN } from '../Context';
import Loader from './common/Loader';


const PHONE_MASK = '+{7} (000) 000-00-00';
// Длина номера, полностью заполненного по маске: +7 (000) 000-00-00
const PHONE_LENGTH = 18;

const EMPTY_FORM = {
  sender_address: '',
  sender_name: '',
  sender_phone: '',
  pickup_date: '',
  comment: '',
};


// Сегодняшняя дата в часовом поясе пользователя в формате YYYY-MM-DD, как у input type="date"
function todayIsoDate() {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}


function FieldError({ error }) {
  if (!error) {
    return null;
  }
  return <p className="text-danger mt-1"><small>{error.message}</small></p>;
}


function CourierCallUnavailable({ courierCall }) {
  if (!courierCall.enabled) {
    return <p>Для этого задания вызов курьера не предусмотрен.</p>;
  }
  return <p>Вызов курьера станет доступен после отправки формы на проверку.</p>;
}


export default function CourierRequest() {
  const { taskId } = useParams();
  const api_url = useContext(API_URL);
  const token = useContext(AUTH_TOKEN);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [requestData, setRequestData] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: EMPTY_FORM,
  });

  const courierRequestUrl = `${api_url}/form_task/${taskId}/courier_request`;

  React.useEffect(() => {
    axios_api
      .get(courierRequestUrl, {
        headers: { Authorization: token },
      })
      .then(
        (result) => {
          setRequestData(result.data);
          reset({ ...EMPTY_FORM, sender_address: result.data.sender_address });
          setLoading(false);
        },
        (error) => {
          console.log(error);
          setLoadError(error.response?.status === 404 ?
            'Задание не найдено' :
            'Не удалось загрузить данные для заявки, обновите страницу'
          );
          setLoading(false);
        }
      );
  }, [courierRequestUrl, token, reset]);

  const onSubmit = (data) => {
    setSubmitting(true);
    setFormError('');

    axios_api
      .post(courierRequestUrl, data, {
        headers: { Authorization: token },
      })
      .then(
        (result) => {
          navigate(`/results/tickets/${result.data.ticket_id}`);
        },
        (error) => {
          console.log(error);
          setFormError(typeof error.response?.data === 'string' ?
            error.response.data :
            'Не удалось отправить заявку на вызов курьера'
          );
          setSubmitting(false);
        }
      );
  };

  if (loading) {
    return <Loader />;
  }

  const courierCall = requestData?.courier_call;
  const formAvailable = courierCall?.enabled && courierCall?.available && !courierCall?.ticket_id;

  return (
    <div className="row">
      <div className="col-9 m-3">

        <h3>Вызов курьера</h3>
        {requestData &&
          <h6 className="mt-2">
            {requestData.form_name.replace(/#.*#/g, '')}, цикл {requestData.cycle}
          </h6>
        }
        <p className="mt-2 mb-4">
          <Link to={`/results/task/${taskId}`}>Вернуться к заданию</Link>
        </p>

        {loadError && <p className="text-danger">{loadError}</p>}

        {courierCall?.ticket_id &&
          <p>
            Заявка на вызов курьера отправлена.{' '}
            <Link to={`/results/tickets/${courierCall.ticket_id}`}>Открыть обращение</Link>
          </p>
        }

        {courierCall && !courierCall.ticket_id && !formAvailable &&
          <CourierCallUnavailable courierCall={courierCall} />
        }

        {formAvailable &&
          <div className="lk-form">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="input-group my-3">
                <label className="col-4">
                  <p>Номер лаборатории</p>
                  <input
                    type="text"
                    className="form-control"
                    style={{width: '500px'}}
                    value={requestData.lab_number}
                    disabled
                  />
                </label>
              </div>

              <div className="input-group my-3">
                <label className="col-4">
                  <p>Адрес отправителя</p>
                  <input
                    type="text"
                    className={errors.sender_address ? "form-control is-invalid" : "form-control"}
                    style={{width: '500px'}}
                    maxLength={255}
                    {...register('sender_address', {
                      required: 'Укажите адрес отправителя',
                      validate: value => value.trim() !== '' || 'Укажите адрес отправителя',
                    })}
                  />
                  <FieldError error={errors.sender_address} />
                </label>
              </div>

              <div className="input-group my-3">
                <label className="col-4">
                  <p>ФИО отправителя</p>
                  <input
                    type="text"
                    className={errors.sender_name ? "form-control is-invalid" : "form-control"}
                    style={{width: '500px'}}
                    maxLength={200}
                    {...register('sender_name', {
                      required: 'Укажите ФИО отправителя',
                      validate: value => value.trim() !== '' || 'Укажите ФИО отправителя',
                    })}
                  />
                  <FieldError error={errors.sender_name} />
                </label>
              </div>

              <div className="input-group my-3">
                <label className="col-4">
                  <p>Телефон отправителя</p>
                  <Controller
                    name="sender_phone"
                    control={control}
                    rules={{
                      required: 'Укажите телефон отправителя',
                      validate: value => value.length === PHONE_LENGTH || 'Телефон должен содержать 10 цифр после +7',
                    }}
                    render={({ field }) => (
                      <IMaskInput
                        mask={PHONE_MASK}
                        value={field.value}
                        unmask={false}
                        onAccept={(value) => field.onChange(value)}
                        onBlur={field.onBlur}
                        inputRef={field.ref}
                        className={errors.sender_phone ? "form-control is-invalid" : "form-control"}
                        style={{width: '500px'}}
                        placeholder="+7(___) ___-__-__"
                      />
                    )}
                  />
                  <FieldError error={errors.sender_phone} />
                </label>
              </div>

              <div className="input-group my-3">
                <label className="col-4">
                  <p>Дата вызова курьера</p>
                  <input
                    type="date"
                    className={errors.pickup_date ? "form-control is-invalid" : "form-control"}
                    style={{width: '500px'}}
                    min={todayIsoDate()}
                    {...register('pickup_date', {
                      required: 'Укажите дату вызова курьера',
                      validate: value => value >= todayIsoDate() || 'Дата не может быть раньше сегодняшней',
                    })}
                  />
                  <FieldError error={errors.pickup_date} />
                </label>
              </div>

              <div className="input-group my-3">
                <label className="col-4">
                  <p>Комментарий</p>
                  <textarea
                    className="form-control"
                    rows="3"
                    style={{width: '500px'}}
                    {...register('comment')}
                  ></textarea>
                </label>
              </div>

              {formError && <p className="text-danger">{formError}</p>}

              <button type="submit" className="btn lk-button my-3" disabled={submitting}>
                Отправить заявку
              </button>
            </form>
          </div>
        }
      </div>
    </div>
  );
}
